import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Wordmark } from '../lib/ui';
import { Icon } from '../lib/icons';
import { useAuth } from '../auth/AuthContext';
import { useSeo } from '../lib/useSeo';
import { track } from '../lib/analytics';

// หน้าขายสำหรับ "ท้าวแชร์" (host) — positioning: เครื่องมือจัดการวงให้ง่าย
// (utility-first) โดยมีหลักฐานตรวจสอบได้เป็นผลพลอยได้/คูเมือง.

const PAINS = [
  { icon: 'wallet', pain: 'จดเองว่าใครจ่ายแล้ว/ยังไม่จ่าย', fix: 'แตะปุ่มเดียว “จ่ายแล้ว” — ระบบจดให้ + เก็บเป็นหลักฐานอัตโนมัติ' },
  { icon: 'coin', pain: 'คิดดอกหัก/ดอกตามเองทุกงวด กลัวผิด', fix: 'คำนวณดอก + ยอดรับให้อัตโนมัติ ทั้งวงเห็นตรงกัน' },
  { icon: 'info', pain: 'ทวงเงินคนค้างจ่ายแบบเกรงใจ', fix: 'กดปุ่มเดียวเตือนทุกคนที่ยังไม่จ่ายพร้อมกัน' },
  { icon: 'shield', pain: 'อยากดูน่าเชื่อถือ หาสมาชิกวงต่อไปง่ายขึ้น', fix: 'มีเครดิต + รีวิว + หลักฐานทุก event ที่ตรวจสอบได้' },
];

const FEATURES = [
  { icon: 'wallet', h: 'กระดานจ่ายเงินงวดนี้', s: 'เห็นใครจ่าย/ค้างในหน้าเดียว แตะยืนยันทีละคน ระบบลงหลักฐานให้เอง' },
  { icon: 'coin', h: 'คิดดอก & ยอดรับอัตโนมัติ', s: 'ดอกหัก/ดอกตาม คำนวณให้ทุกงวด ไม่ต้องกดเครื่องคิดเลข' },
  { icon: 'arrowUp', h: 'บันทึกผลเปีย / จับสลากยุติธรรม', s: 'วงเงินสดบันทึกผลเปีย · วงซื้อของสุ่มผู้รับแบบตรวจสอบย้อนหลังได้' },
  { icon: 'user', h: 'อนุมัติสมาชิก + ดูเครดิต', s: 'เห็นเครดิต/ความตรงเวลาก่อนรับเข้าวง อนุมัติ/ปฏิเสธในคลิกเดียว' },
  { icon: 'send', h: 'เชิญเข้าวงด้วยลิงก์/QR', s: 'สร้างวงเสร็จส่ง QR ให้เพื่อนสแกนเข้าร่วมได้ทันที' },
  { icon: 'shield', h: 'หลักฐานตรวจสอบได้ฟรี', s: 'ทุก event ลงลายเซ็นดิจิทัล มีปัญหาเมื่อไหร่ออกซองหลักฐานได้เลย' },
];

const STEPS = [
  { n: '1', h: 'ตั้งวงใน 2 นาที', s: 'ใส่ชื่อ ยอด จำนวนคน — ระบบตรวจกฎ พ.ร.บ. 2534 ให้อัตโนมัติ' },
  { n: '2', h: 'เชิญสมาชิกด้วย QR', s: 'ส่งลิงก์/QR ให้เพื่อนร่วมงาน/คนรู้จักสแกนเข้าร่วม' },
  { n: '3', h: 'จัดการแบบ autopilot', s: 'แตะยืนยันการจ่าย เตือนคนค้าง บันทึกผลเปีย — ที่เหลือระบบจดให้' },
];

const FAQS = [
  { q: 'ใช้เป็นท้าวแชร์ฟรีจริงไหม?', a: 'ฟรีตลอดไป — ทั้งท้าวและลูกแชร์ ไม่มีค่าธรรมเนียม ไม่หักเปอร์เซ็นต์จากกอง' },
  { q: 'Peershare ถือเงินวงของฉันไหม?', a: 'ไม่ถือเงินเด็ดขาด คุณเก็บเงิน/โอน/นัดเจอกันเองตามปกติ เราแค่ช่วยจัดการและบันทึกหลักฐานให้' },
  { q: 'สมาชิกต้องโหลดแอปไหม?', a: 'เข้าผ่านลิงก์/QR ได้เลย ใช้บนเว็บมือถือ ติดตั้งเป็นแอป (PWA) ก็ได้ถ้าต้องการ' },
  { q: 'ถ้ามีคนเบี้ยวจะทำยังไง?', a: 'ทุกการจ่าย/ผลเปียถูกบันทึกแบบแก้ไม่ได้ คุณ (หรือสมาชิก) ขอ “ซองหลักฐานปิดผนึก” ไปใช้เจรจา/ดำเนินคดีได้' },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button onClick={() => setOpen(o => !o)} className="ps-press"
      style={{ textAlign: 'left', width: '100%', background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 14, padding: '16px 18px', fontFamily: PS.fontUI, cursor: 'pointer' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600, letterSpacing: -0.2, color: PS.text }}>{q}</div>
        <div style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }}>
          <Icon.chevronD size={18} stroke={PS.textMuted} />
        </div>
      </div>
      {open && <div className="ps-fade" style={{ fontSize: 13, color: PS.textMuted, lineHeight: 1.65, marginTop: 10 }}>{a}</div>}
    </button>
  );
}

export default function HostLanding() {
  const nav = useNavigate();
  const { login } = useAuth();

  useEffect(() => { track('host_landing_view'); }, []);

  useSeo({
    title: 'เปิดวงแชร์ฟรี — เครื่องมือจัดการวงสำหรับท้าวแชร์ | เปียแชร์',
    description: 'เป็นท้าวแชร์ง่ายขึ้นด้วยเปียแชร์ — กระดานจ่ายเงินแตะปุ่มเดียว คิดดอกอัตโนมัติ เตือนคนค้างจ่ายในคลิกเดียว เชิญด้วย QR และเก็บหลักฐานทุก event ฟรีตลอดไป ถูกต้องตาม พ.ร.บ. การเล่นแชร์ 2534',
    path: '/host',
    jsonLd: [
      { '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
    ],
  });

  const startCreate = (loc) => { track('host_cta_click', { loc }); nav('/app/create'); };

  return (
    <div style={{ minHeight: '100vh', background: PS.page, color: PS.text, fontFamily: PS.fontUI }}>
      {/* Nav */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '22px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={() => nav('/')} className="ps-press" style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer' }} aria-label="กลับหน้าหลัก"><Wordmark size={24} /></button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button data-testid="host-nav-home" onClick={() => nav('/')} className="ps-press"
            style={{ border: 'none', background: 'transparent', color: PS.textMuted, fontSize: 13, fontWeight: 500, fontFamily: PS.fontUI }}>
            สำหรับลูกแชร์
          </button>
          <button data-testid="host-login-btn" onClick={login} className="ps-press"
            style={{ background: PS.ink, color: PS.page, border: 'none', padding: '10px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: PS.fontUI }}>
            เข้าสู่ระบบ
          </button>
        </div>
      </div>

      {/* Hero */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '36px 24px 24px' }}>
        <div className="ps-fade-up" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22 }}>
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '6px 12px', borderRadius: 999, background: 'oklch(0.62 0.12 38 / 0.14)', color: PS.terracotta, fontSize: 12, fontWeight: 700 }}>
            <Icon.coin size={13} stroke={PS.terracotta} /> สำหรับท้าวแชร์
          </div>
          <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center', padding: '6px 12px', borderRadius: 999, background: PS.ink, color: PS.page, fontSize: 12, fontWeight: 700 }}>
            <Icon.starO size={13} stroke="oklch(0.82 0.12 82)" /> ฟรีตลอดไป
          </div>
        </div>
        <h1 className="ps-fade-up" style={{ fontSize: 'clamp(34px, 6vw, 58px)', fontWeight: 700, letterSpacing: -1.5, lineHeight: 1.06, margin: 0, maxWidth: 820 }}>
          จัดการวงแชร์ให้ง่าย<br />
          <span style={{ color: PS.textMuted }}>เหมือนมี</span><span style={{ color: PS.terracotta }}>ผู้ช่วยส่วนตัว</span>
        </h1>
        <p className="ps-fade-up" style={{ fontSize: 'clamp(15px,2.4vw,19px)', color: PS.textMuted, lineHeight: 1.6, marginTop: 20, maxWidth: 640 }}>
          เลิกจดในสมุด เลิกกดเครื่องคิดเลข เลิกทวงเงินแบบเกรงใจ —
          <b style={{ color: PS.text }}> เปียแชร์</b> ช่วยท้าวจัดการทั้งวงในที่เดียว
          แตะปุ่มเดียวยืนยันการจ่าย คิดดอกให้อัตโนมัติ เตือนคนค้างได้ทันที
          และเก็บ<b style={{ color: PS.text }}>หลักฐานทุก event</b>ให้ฟรี
        </p>
        <div className="ps-fade-up" style={{ display: 'flex', gap: 12, marginTop: 28, flexWrap: 'wrap' }}>
          <button data-testid="host-hero-create" onClick={() => startCreate('hero')} className="ps-press"
            style={{ background: PS.ink, color: PS.page, border: 'none', padding: '15px 28px', borderRadius: 999, fontSize: 15, fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center', fontFamily: PS.fontUI }}>
            ตั้งวงแรกฟรี <Icon.chevronR size={16} stroke={PS.page} />
          </button>
          <button data-testid="host-hero-login" onClick={login} className="ps-press"
            style={{ background: 'transparent', color: PS.text, border: `1px solid ${PS.line}`, padding: '15px 24px', borderRadius: 999, fontSize: 15, fontWeight: 500, fontFamily: PS.fontUI }}>
            เข้าสู่ระบบด้วย Google
          </button>
        </div>
        <div style={{ fontSize: 12, color: PS.textDim, marginTop: 12 }}>ตั้งวงได้ภายใน 2 นาที · ระบบตรวจกฎ พ.ร.บ. 2534 ให้อัตโนมัติ</div>
      </div>

      {/* Pain → Fix */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '36px 24px 8px' }}>
        <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 18 }}>ปัญหาที่ท้าวแชร์เจอทุกงวด — เราช่วยได้</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          {PAINS.map((p, i) => {
            const Ico = Icon[p.icon] || Icon.shield;
            return (
              <div key={i} className="ps-fade-up" style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 16, padding: 20 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: 'oklch(0.62 0.12 38 / 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <Ico size={18} stroke={PS.terracotta} />
                </div>
                <div style={{ fontSize: 13.5, color: PS.textMuted, textDecoration: 'line-through', textDecorationColor: PS.line }}>{p.pain}</div>
                <div style={{ display: 'flex', gap: 7, alignItems: 'flex-start', marginTop: 8 }}>
                  <Icon.check size={15} stroke="oklch(0.42 0.08 155)" sw={2.5} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.45 }}>{p.fix}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Features */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '40px 24px 8px' }}>
        <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 18 }}>เครื่องมือคอนโซลท้าวแชร์</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {FEATURES.map((f, i) => {
            const Ico = Icon[f.icon] || Icon.shield;
            return (
              <div key={i} className="ps-fade-up" style={{ background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 16, padding: 22 }}>
                <Ico size={22} stroke={PS.ink} />
                <div style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: -0.2, marginTop: 12 }}>{f.h}</div>
                <div style={{ fontSize: 13, color: PS.textMuted, marginTop: 6, lineHeight: 1.55 }}>{f.s}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* How it works */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '44px 24px 8px' }}>
        <h2 style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 700, letterSpacing: -0.6, margin: '0 0 20px' }}>เริ่มเป็นท้าวแชร์ใน 3 ขั้น</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {STEPS.map((s) => (
            <div key={s.n} style={{ background: PS.surface2, border: `1px solid ${PS.line}`, borderRadius: 16, padding: 22 }}>
              <div className="num" style={{ width: 34, height: 34, borderRadius: 17, background: PS.ink, color: PS.page, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 600 }}>{s.n}</div>
              <div style={{ fontSize: 15.5, fontWeight: 700, marginTop: 14 }}>{s.h}</div>
              <div style={{ fontSize: 13, color: PS.textMuted, marginTop: 6, lineHeight: 1.55 }}>{s.s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Free forever + CTA */}
      <div style={{ maxWidth: 1120, margin: '0 auto', padding: '44px 24px 12px' }}>
        <div style={{ background: PS.ink, color: PS.page, borderRadius: 24, padding: 'clamp(28px,5vw,48px)', position: 'relative', overflow: 'hidden' }}>
          <div style={{ fontSize: 'clamp(26px,5vw,42px)', fontWeight: 700, letterSpacing: -1.2, lineHeight: 1.08 }}>
            เป็นท้าวแชร์ฟรี<span style={{ color: 'oklch(0.82 0.12 82)' }}>ตลอดไป</span>
          </div>
          <p style={{ fontSize: 'clamp(14px,2vw,17px)', color: 'rgba(245,241,234,0.7)', lineHeight: 1.7, marginTop: 16, maxWidth: 620 }}>
            ไม่มีค่าธรรมเนียม ไม่หักเปอร์เซ็นต์จากกอง — เราเชื่อว่าเครื่องมือจัดการวงที่ดี
            และหลักฐานที่เชื่อถือได้ ควรเข้าถึงได้สำหรับท้าวแชร์ทุกคน
          </p>
          <div style={{ display: 'flex', gap: 22, marginTop: 24, flexWrap: 'wrap' }}>
            {['ทุกฟีเจอร์ใช้ได้เต็ม', 'ตรวจกฎหมายให้อัตโนมัติ', 'ไม่ถือเงิน — คุณคุมเอง'].map((t) => (
              <div key={t} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13.5, color: 'rgba(245,241,234,0.85)' }}>
                <div style={{ width: 18, height: 18, borderRadius: 9, background: 'oklch(0.48 0.08 155)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon.check size={12} stroke="#fff" sw={2.5} /></div>{t}
              </div>
            ))}
          </div>
          <button data-testid="host-cta-create" onClick={() => startCreate('footer')} className="ps-press"
            style={{ marginTop: 28, background: PS.page, color: PS.ink, border: 'none', padding: '15px 28px', borderRadius: 999, fontSize: 15, fontWeight: 700, display: 'inline-flex', gap: 8, alignItems: 'center', fontFamily: PS.fontUI }}>
            ตั้งวงแรกของคุณ <Icon.chevronR size={16} stroke={PS.ink} />
          </button>
          <div style={{ position: 'absolute', right: -40, bottom: -40, width: 200, height: 200, border: '1px solid rgba(245,241,234,0.07)', transform: 'rotate(45deg)' }} />
        </div>
      </div>

      {/* FAQ */}
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '44px 24px 8px' }}>
        <h2 style={{ fontSize: 'clamp(22px,4vw,32px)', fontWeight: 700, letterSpacing: -0.6, margin: '0 0 20px' }}>ท้าวแชร์ถามบ่อย</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQS.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
        </div>
      </div>

      {/* Footer */}
      <footer style={{ background: PS.ink, color: PS.page, marginTop: 40 }}>
        <div style={{ maxWidth: 1120, margin: '0 auto', padding: '36px 24px', display: 'flex', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          <div>
            <Wordmark color={PS.page} size={22} />
            <div style={{ fontSize: 12.5, color: 'rgba(245,241,234,0.6)', marginTop: 10, lineHeight: 1.6, maxWidth: 320 }}>
              คนกลางผู้เก็บหลักฐานของวงแชร์ — ฟรีตลอดไป ถูกต้องตาม พ.ร.บ. การเล่นแชร์ พ.ศ. 2534 · เราไม่ถือเงิน ไม่ตัดสินข้อพิพาท
            </div>
          </div>
          <button onClick={() => startCreate('footer-link')} className="ps-press"
            style={{ background: PS.page, color: PS.ink, border: 'none', padding: '12px 22px', borderRadius: 999, fontSize: 14, fontWeight: 700, fontFamily: PS.fontUI }}>
            ตั้งวงฟรี
          </button>
        </div>
      </footer>
    </div>
  );
}
